import {
  put, take, takeLatest, takeEvery, select, all,
} from 'redux-saga/effects';
import _ from 'lodash';
import {
  TeammoApi,
  normalize,
  compilePaginationObject,
  getStoredUser,
} from 'app/utils/redux/helpers';
import history from 'app/routes/history';
import localStorage from 'local-storage';
import { getUserId } from 'app/ducks/authentication/selectors';
import { clubSchema, clubListSchema } from 'app/utils/redux/schemas';
import { handleError } from 'app/utils/handlers/handleError';
import { loadingStart, loadingFinish } from 'app/ducks/loading/actions';
import {
  sagaActions, selectClub, selectLastClub, getAllClubs,
} from 'app/ducks/clubs/actions';
import { ClubsActionTypes as types } from 'app/ducks/clubs/types';

import { getAllTeamMembers } from 'app/ducks/teamMembers/actions';
import { getOrganisation } from 'app/ducks/organisations/actions';
import { getAllGroups, getGroup } from 'app/ducks/groups/actions';
import { getAllOrganisationSections } from 'app/ducks/organisationSections/actions';
import { getAllPayments } from 'app/ducks/payments/actions';

import { IStoredUser } from 'app/types/common';
import IApplicationState from '../../types/state';

function* createClubSaga(action): any {
  yield put(sagaActions.createClubPending(action.payload));
  const [error, response] = yield (yield TeammoApi(yield select())).clubs.create(
    action.payload,
  );

  if (response) {
    const normalizedResponse = normalize(response, clubSchema);

    yield put(sagaActions.createClubSuccess(normalizedResponse));
    history.push('/dashboard');
  } else {
    yield put(
      handleError(
        error,
        sagaActions.createClubFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* updateClubSaga(action): any {
  yield put(sagaActions.updateClubPending(action.payload));
  const [error, response] = yield (yield TeammoApi(yield select())).clubs.update(
    action.payload,
  );

  if (response) {
    const normalizedResponse = normalize(response, clubSchema);

    yield put(sagaActions.updateClubSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.updateClubFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* deleteClubSaga(action): any {
  yield put(sagaActions.deleteClubPending());
  const [error] = yield (yield TeammoApi(yield select())).clubs.delete(action.payload);

  if (error) {
    yield put(
      handleError(
        error,
        sagaActions.deleteClubFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  } else {
    yield put(sagaActions.deleteClubSuccess(yield getUserId()));

    yield put(yield selectLastClub());
  }
}

function* getClubSaga(action): any {
  yield put(sagaActions.getClubPending());
  const [error, response] = yield (yield TeammoApi(yield select())).clubs.show(
    action.payload,
  );

  if (response) {
    const normalizedResponse = normalize(response, clubSchema);
    yield put(sagaActions.getClubSuccess(normalizedResponse));
    return normalizedResponse.entities.clubs[normalizedResponse.result];
  }
  history.push('/clubs');
  yield put(
    handleError(error, sagaActions.getClubFailed(_.isEmpty(error) ? {} : error.response.data.errors)),
  );
  return null;
}

function* getAllClubsSaga(action): any {
  yield put(
    sagaActions.getAllClubsPending(action.payload.compile ? action.payload.pagination : {}),
  );
  yield put(loadingStart());

  const paginationObject = action.payload.compile
    ? compilePaginationObject(yield select((state) => state.clubs.pagination))
    : action.payload.pagination;

  const [error, response] = yield (yield TeammoApi(yield select())).clubs.list(
    paginationObject,
  );

  if (response) {
    const normalizedResponse = normalize(response, clubListSchema);

    yield put(
      sagaActions.getAllClubsSuccess(normalizedResponse, response.meta),
    );
    yield put(loadingFinish());
  } else {
    yield put(error, sagaActions.getAllClubsFailed(_.isEmpty(error) ? {} : error.response.data.errors));
  }
}

function* selectLastClubSaga(): any {
  yield put(sagaActions.selectLastClubPending());
  const storedUser: IStoredUser = yield getStoredUser();
  // let currentClubId: string = getCurrentClubId(yield select(), storedUser);
  const storedClubId = storedUser.currentClubId;
  const stateClubId = yield select(
    (state: IApplicationState) => state.clubs.currentClubId,
  );
  let currentClubId = storedClubId || stateClubId;
  if (storedClubId === undefined && stateClubId === undefined) {
    yield put(yield getAllClubs());
    yield take(types.GET_ALL_CLUBS_SUCCESS);

    const clubs = yield select((state) => state.clubs);
    if (clubs.clubsLoaded && clubs.clubIds.length > 0) {
      currentClubId = _.head(clubs.clubIds);
    }
  }
  if (currentClubId === undefined) {
    history.push('/create-club');
    yield put(sagaActions.selectLastClubFinished(currentClubId));
    return true;
  }
  const club = yield* getClubSaga({ payload: currentClubId });
  if (_.isEmpty(club)) {
    localStorage.set(yield getUserId(), {});
    yield* selectLastClubSaga();
    return false;
  }
  if (club.organisationId != null) {
    yield all([
      put(getAllGroups(club.organisationId)),
      put(yield getAllOrganisationSections(club.organisationId)),
      put(yield getOrganisation(club.organisationId)),
    ]);
  }

  if (club.group !== undefined && club.group.id !== null) {
    yield put(yield getGroup(club.group.id));
  }
  yield all([
    put(yield getAllTeamMembers(club.id)),
    put(yield getAllPayments()),
    put(selectClub(club.id)),
  ]);

  const currentClub = yield select(
    (state) => state.clubs.clubsById[state.clubs.currentClubId],
  );
  if (currentClubId !== undefined && _.isEmpty(currentClub)) {
    yield take(types.GET_CLUB_SUCCESS);
  }
  yield put(sagaActions.selectLastClubFinished(currentClubId));
}

function* selectClubSaga(action): any {
  const userId = yield getUserId();
  const clubId = action.payload.currentClubId;

  localStorage.set(userId, {
    currentClubId: clubId,
  });
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createClubSagaWatcher(): any {
  yield takeLatest(types.CREATE_CLUB_REQUESTED, createClubSaga);
}

function* updateClubSagaWatcher(): any {
  yield takeLatest(types.UPDATE_CLUB_REQUESTED, updateClubSaga);
}

function* deleteClubSagaWatcher(): any {
  yield takeLatest(types.DELETE_CLUB_REQUESTED, deleteClubSaga);
}

function* getClubSagaWatcher(): any {
  yield takeEvery(types.GET_CLUB_REQUESTED, getClubSaga);
}

function* getAllClubsSagaWatcher(): any {
  yield takeEvery(types.GET_ALL_CLUBS_REQUESTED, getAllClubsSaga);
}

function* selectLastClubSagaWatcher(): any {
  yield takeLatest(types.SELECT_LAST_CLUB_REQUESTED, selectLastClubSaga);
}

function* selectClubSagaWatcher(): any {
  yield takeLatest(types.SELECT_CLUB_REQUESTED, selectClubSaga);
}

export default function* root(): any {
  yield all([
    createClubSagaWatcher(),
    updateClubSagaWatcher(),
    deleteClubSagaWatcher(),
    getClubSagaWatcher(),
    getAllClubsSagaWatcher(),
    selectLastClubSagaWatcher(),
    selectClubSagaWatcher(),
  ]);
}
